import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  DollarSign,
  Image as ImageIcon,
  Loader2,
  Package,
  Percent,
  Sparkles,
  Tag,
  UploadCloud,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { apiGet, apiPatch, apiPost, ApiError } from "@/services/api";

type FormState = {
  title: string;
  slug: string;
  brand: string;
  category: string;
  description: string;
  images: string[];
  price: string;
  mrp: string;
  discount: string;
  stock: string;
  tags: string;
};

const emptyForm: FormState = {
  title: "",
  slug: "",
  brand: "",
  category: "",
  description: "",
  images: [],
  price: "",
  mrp: "",
  discount: "0",
  stock: "0",
  tags: "",
};

/**
 * Client-side photo optimizer: resizes and compresses image files into efficient
 * Web/JPEG data URLs for immediate preview and reliable storage without external upload bucket dependency.
 */
function compressImageFile(file: File, maxWidth = 1000, quality = 0.78): Promise<string> {
  return new Promise((resolve, reject) => {
    const isImage = file.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif)$/i.test(file.name);
    if (!isImage) {
      reject(new Error("File is not an image"));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Failed to load image"));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxWidth) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxWidth) / height);
            height = maxWidth;
          }
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(e.target?.result as string);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        resolve(dataUrl);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/** Automatically calculate discount percentage when both selling price and MRP are provided. */
function calculateDiscount(sellingPrice: string, mrpPrice: string): string {
  const p = parseFloat(sellingPrice);
  const m = parseFloat(mrpPrice);
  if (!isNaN(p) && !isNaN(m) && m > 0 && m > p) {
    const raw = Math.round(((m - p) / m) * 100);
    return String(Math.min(95, Math.max(0, raw)));
  }
  return "0";
}

export function VendorProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [categories, setCategories] = useState<string[]>([]);
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing product if in edit mode
  useEffect(() => {
    if (!id) return;
    void apiGet<{ products: (Record<string, unknown> & { id: string })[] }>("/api/vendors/me/products").then((d) => {
      const p = d?.products?.find((x) => x.id === id);
      if (!p) return;
      const initialImages = Array.isArray(p.images)
        ? (p.images as string[]).filter(Boolean)
        : typeof p.images === "string"
        ? (p.images as string).split("\n").filter(Boolean)
        : [];

      setForm({
        title: String(p.title ?? ""),
        slug: String(p.slug ?? ""),
        brand: String(p.brand ?? ""),
        category: String(p.category ?? ""),
        description: String(p.description ?? ""),
        images: initialImages,
        price: String(p.price ?? ""),
        mrp: p.mrp ? String(p.mrp) : "",
        discount: String(p.discount ?? "0"),
        stock: String(p.stock ?? "0"),
        tags: Array.isArray(p.tags) ? (p.tags as string[]).join(", ") : "",
      });
      setSlugTouched(true);
    });
  }, [id]);

  // Load marketplace category suggestions
  useEffect(() => {
    void apiGet<{ categories?: string[] }>("/api/categories")
      .then((d) => {
        if (d?.categories && Array.isArray(d.categories)) {
          setCategories(d.categories);
        }
      })
      .catch(() => {});
  }, []);

  // Title change with auto-slug generation
  function handleTitleChange(val: string) {
    setForm((prev) => {
      const next: FormState = { ...prev, title: val };
      if (!slugTouched) {
        next.slug = val
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 80);
      }
      return next;
    });
  }

  // Price change with auto discount calculation
  function handlePriceChange(val: string) {
    setForm((prev) => {
      const nextDiscount = prev.mrp ? calculateDiscount(val, prev.mrp) : "0";
      return {
        ...prev,
        price: val,
        discount: nextDiscount,
      };
    });
  }

  // MRP change with auto discount calculation (MRP is optional)
  function handleMrpChange(val: string) {
    setForm((prev) => {
      const nextDiscount = val ? calculateDiscount(prev.price, val) : "0";
      return {
        ...prev,
        mrp: val,
        discount: nextDiscount,
      };
    });
  }

  // Handle local file browser & drag-and-drop photo selection
  async function handleFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter(
      (f) => f.type.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp|avif|heic|heif)$/i.test(f.name)
    );
    if (validFiles.length === 0) {
      toast.error("Please select valid image files (JPG, PNG, WebP)");
      return;
    }

    setIsProcessingPhotos(true);
    const toastId = toast.loading(`Optimizing ${validFiles.length} photo${validFiles.length > 1 ? "s" : ""}…`);

    try {
      const processed: string[] = [];
      for (const file of validFiles) {
        try {
          const dataUrl = await compressImageFile(file);
          processed.push(dataUrl);
        } catch (err) {
          console.error("Failed to compress file:", file.name, err);
        }
      }

      if (processed.length > 0) {
        setForm((prev) => ({
          ...prev,
          images: [...prev.images, ...processed],
        }));
        toast.success(`Added ${processed.length} photo${processed.length > 1 ? "s" : ""}`, { id: toastId });
      } else {
        toast.dismiss(toastId);
      }
    } catch {
      toast.error("Failed to process selected photos", { id: toastId });
    } finally {
      setIsProcessingPhotos(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  // Remove photo
  function handleRemoveImage(index: number) {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  }

  // Set as primary/cover image
  function handleSetAsCover(index: number) {
    if (index === 0) return;
    setForm((prev) => {
      const target = prev.images[index];
      const others = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: [target, ...others],
      };
    });
    toast.success("Set as primary product photo");
  }

  // Form submission
  async function submit(e: React.FormEvent) {
    e.preventDefault();

    if (form.title.trim().length < 2) {
      toast.error("Product title must be at least 2 characters");
      return;
    }

    if (!form.category.trim()) {
      toast.error("Please select or enter a category");
      return;
    }

    const sellingPrice = parseFloat(form.price);
    if (isNaN(sellingPrice) || sellingPrice <= 0) {
      toast.error("Selling price must be greater than 0");
      return;
    }

    setSaving(true);
    try {
      const parsedMrp = form.mrp.trim() ? parseFloat(form.mrp) : undefined;
      const parsedDiscount = parseFloat(form.discount);
      const parsedStock = parseInt(form.stock, 10);

      // Safe fallback slug generation if user left it blank or auto-slug was empty (e.g. Amharic / non-latin)
      let safeSlug = form.slug.trim();
      if (!safeSlug) {
        safeSlug = form.title
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 80);
      }
      if (!safeSlug || safeSlug.length < 3) {
        safeSlug = `prod-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      }

      const defaultPlaceholder = "https://images.unsplash.com/photo-1523275335684-37898b6baf30";
      const finalImages = form.images.length > 0 ? form.images : [defaultPlaceholder];

      const payload = {
        title: form.title.trim(),
        slug: safeSlug,
        brand: form.brand.trim() || "Generic",
        category: form.category.trim(),
        description: form.description.trim() || form.title.trim(),
        images: finalImages,
        price: sellingPrice,
        ...(parsedMrp && parsedMrp > 0 ? { mrp: parsedMrp } : { mrp: sellingPrice }),
        discount: !isNaN(parsedDiscount) ? Math.round(Math.min(95, Math.max(0, parsedDiscount))) : 0,
        stock: !isNaN(parsedStock) ? Math.max(0, parsedStock) : 0,
        tags: Array.from(
          new Set(
            form.tags
              .split(",")
              .map((s) => s.trim().toLowerCase())
              .filter(Boolean)
          )
        ),
      };

      if (isEdit) {
        await apiPatch(`/api/vendors/me/products/${id}`, payload);
        toast.success("Product updated — sent to admin for review!");
      } else {
        await apiPost("/api/vendors/me/products", payload);
        toast.success("Product saved and sent to admin for review!");
      }

      navigate("/vendor/products");
    } catch (err: unknown) {
      console.error("Product submit error:", err);
      let msg = "Could not save product";
      if (err instanceof ApiError) {
        msg = err.message;
        if (err.body && typeof err.body === "object") {
          const bodyObj = err.body as { message?: unknown };
          if (typeof bodyObj.message === "string") msg = bodyObj.message;
          else if (Array.isArray(bodyObj.message)) msg = bodyObj.message.join(", ");
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  }

  const savingsAmount =
    form.mrp && form.price && parseFloat(form.mrp) > parseFloat(form.price)
      ? (parseFloat(form.mrp) - parseFloat(form.price)).toLocaleString()
      : null;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Back Link & Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/vendor/products"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#087DB5] hover:text-[#073B70] hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Products
          </Link>
          <h1 className="mt-1 text-2xl font-black text-slate-900 dark:text-white">
            {isEdit ? "Edit Product" : "Add New Product"}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Provide product details, photos, and pricing. Products are published after review.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="amazon-card space-y-6 p-5 sm:p-7">
        {/* SECTION 1: BASIC INFORMATION */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2 dark:border-white/10">
            <Package className="h-4 w-4 text-[#087DB5]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              1. Basic Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Title */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Product Title <span className="text-rose-500">*</span>
              </label>
              <input
                required
                minLength={2}
                value={form.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="e.g. Apple iPhone 15 Pro Max 256GB Natural Titanium"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Slug */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                  URL Slug
                </label>
                <span className="text-[10px] text-slate-400 font-normal">Optional</span>
              </div>
              <input
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((f) => ({ ...f, slug: e.target.value }));
                }}
                placeholder="Auto-generated if empty"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-xs sm:text-sm font-mono text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Brand */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                  Brand / Manufacturer
                </label>
                <span className="text-[10px] text-slate-400 font-normal">Optional</span>
              </div>
              <input
                value={form.brand}
                onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))}
                placeholder="e.g. Apple, Samsung, Generic"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Category with Datalist */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Category <span className="text-rose-500">*</span>
              </label>
              <input
                required
                list="category-suggestions"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                placeholder="Select or enter primary category (e.g. Electronics, Mobiles, Fashion)"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
              <datalist id="category-suggestions">
                {categories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                  Description
                </label>
                <span className="text-[10px] text-slate-400 font-normal">Optional</span>
              </div>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Detail key specs, warranty, box contents, and features..."
                className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50/60 p-3 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>
        </div>

        {/* SECTION 2: PRODUCT PHOTOS & UPLOAD */}
        <div className="space-y-3">
          <div className="flex items-center justify-between border-b border-slate-200 pb-2 dark:border-white/10">
            <div className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-[#087DB5]" />
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
                2. Product Images
              </h2>
            </div>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">
              {form.images.length} photo{form.images.length === 1 ? "" : "s"} added
            </span>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />

          {/* Device Browse / Drag & Drop Dropzone */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              if (e.dataTransfer.files) {
                handleFiles(e.dataTransfer.files);
              }
            }}
            className={`group flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
              isDragging
                ? "border-[#087DB5] bg-sky-50 shadow-sm scale-[1.01] dark:bg-sky-950/40"
                : "border-slate-300 bg-slate-50/60 hover:border-[#087DB5] hover:bg-sky-50/30 dark:border-white/15 dark:bg-slate-800/40"
            }`}
          >
            {isProcessingPhotos ? (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-[#087DB5] dark:bg-sky-950/60">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
                <p className="mt-2 text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                  Optimizing device photos…
                </p>
                <p className="text-[11px] text-slate-400">Preparing high-resolution images</p>
              </>
            ) : (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-sky-100 text-[#087DB5] transition group-hover:scale-110 dark:bg-sky-950/60">
                  <UploadCloud className="h-6 w-6" />
                </div>
                <p className="mt-2 text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                  Click to browse photos or drag &amp; drop files here
                </p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Select JPG, PNG, or WebP photos from your device storage.
                </p>
              </>
            )}
          </div>

          {/* Photo Thumbnails Preview Grid */}
          {form.images.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {form.images.map((img, idx) => (
                <div
                  key={idx}
                  className="group relative h-24 w-24 overflow-hidden rounded-xl border-2 border-slate-200 bg-slate-100 shadow-xs dark:border-white/10 dark:bg-slate-800"
                >
                  <img
                    src={img}
                    alt={`Product preview ${idx + 1}`}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLElement).style.opacity = "0.3";
                    }}
                  />

                  {/* Primary Cover Badge */}
                  {idx === 0 ? (
                    <span className="absolute bottom-1 left-1 rounded bg-[#087DB5] px-1.5 py-0.5 text-[9px] font-black uppercase text-white shadow-xs">
                      Cover
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSetAsCover(idx)}
                      className="absolute bottom-1 left-1 hidden group-hover:inline-block rounded bg-black/70 px-1 py-0.5 text-[9px] font-bold text-white backdrop-blur-xs hover:bg-[#087DB5]"
                      title="Set as main cover image"
                    >
                      Make Cover
                    </button>
                  )}

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(idx)}
                    className="absolute top-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-white shadow-xs opacity-90 hover:opacity-100 transition"
                    title="Remove photo"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* SECTION 3: PRICING & INVENTORY */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2 dark:border-white/10">
            <DollarSign className="h-4 w-4 text-[#087DB5]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              3. Pricing &amp; Stock
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
            {/* Selling Price */}
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Selling Price (ETB) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                step="any"
                min="1"
                required
                value={form.price}
                onChange={(e) => handlePriceChange(e.target.value)}
                placeholder="e.g. 4500"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm font-bold text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* MRP (Non-Mandatory / Optional) */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                  MRP / Market Price
                </label>
                <span className="text-[10px] text-slate-400 font-normal">Optional</span>
              </div>
              <input
                type="number"
                step="any"
                min="1"
                value={form.mrp}
                onChange={(e) => handleMrpChange(e.target.value)}
                placeholder="e.g. 5200"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Discount % (Auto-calculated when MRP is added) */}
            <div>
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                  Discount %
                </label>
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                  Auto-calculated
                </span>
              </div>
              <div className="relative mt-1">
                <input
                  type="number"
                  min="0"
                  max="95"
                  value={form.discount}
                  onChange={(e) => setForm((f) => ({ ...f, discount: e.target.value }))}
                  placeholder="0"
                  className="h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 pr-8 text-sm font-bold text-emerald-700 dark:text-emerald-300 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800"
                />
                <Percent className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              </div>
            </div>

            {/* Stock */}
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Available Stock <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                required
                value={form.stock}
                onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))}
                placeholder="1"
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm font-semibold text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>
          </div>

          {/* Live Discount Indicator */}
          {savingsAmount && Number(form.discount) > 0 && (
            <div className="flex items-center gap-2 rounded-xl bg-emerald-50 p-2.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/40">
              <Sparkles className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>
                Discount automatically computed: <strong>{form.discount}% OFF</strong> (Buyer saves {savingsAmount} ETB compared to MRP).
              </span>
            </div>
          )}
        </div>

        {/* SECTION 4: TAGS & METADATA */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2 dark:border-white/10">
            <Tag className="h-4 w-4 text-[#087DB5]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              4. Search Tags
            </h2>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
              Tags <span className="font-normal text-slate-400">(Comma separated)</span>
            </label>
            <input
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="e.g. smartphone, 5g, 256gb, oled, flagship"
              className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/vendor/products")}
            disabled={saving}
            className="w-full sm:w-auto h-10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={saving || isProcessingPhotos}
            className="w-full sm:w-auto h-10 bg-gradient-to-r from-[#087DB5] to-[#073B70] hover:from-[#098ec7] hover:to-[#08488a] font-bold text-white px-6 shadow-sm active:scale-95 transition"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                Saving Product…
              </>
            ) : isEdit ? (
              "Save Changes"
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-1.5" />
                Submit for Review
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
